import styled, { css } from "styled-components";
import { borderRadius, fontWeights, lineHeights, mediaQueries, spacing, typographyMixin } from "@magicbi/ui-library";
import type { GroupedTableTone } from "./GroupedTable.types";

const phone = mediaQueries.down.md;

export const Wrap = styled.div`
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  border-radius: ${borderRadius.xl};
  background: ${({ theme }) => theme.colors.background.primary};
  ${phone} {
    overflow: visible;
    border: 0;
    border-radius: 0;
    background: none;
  }
`;

export const Table = styled.table`
  width: 100%;
  min-width: 46rem;
  border-collapse: collapse;
  ${phone} {
    display: block;
    min-width: 0;
  }
`;

export const Caption = styled.caption`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

export const Head = styled.thead`
  ${phone} {
    display: none;
  }
`;

export const HeadCell = styled.th<{ $minWidth?: string; $fit?: boolean; $plain?: boolean }>`
  padding: ${spacing[2]} ${spacing[3]};
  background: ${({ theme }) => theme.colors.background.secondary};
  text-align: left;
  ${typographyMixin("caption", 2)}
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
  min-width: ${({ $minWidth }) => $minWidth ?? "auto"};
  width: ${({ $fit }) => ($fit ? "1%" : "auto")};
  ${({ $plain }) =>
    $plain &&
    css`
      padding: ${spacing[2.5]} ${spacing[3.5]};
      background: ${({ theme }) => theme.colors.surface.primary};
      border-bottom: 1px solid ${({ theme }) => theme.colors.border.subtle};
      line-height: ${lineHeights["18"]};
      text-transform: none;
      letter-spacing: normal;
    `}
`;

export const Body = styled.tbody`
  ${phone} {
    display: block;
    margin-bottom: ${spacing[3]};
  }
`;

export const GroupHead = styled.th`
  padding: ${spacing[2]} ${spacing[3]};
  border-top: 1px solid ${({ theme }) => theme.colors.border.subtle};
  background: ${({ theme }) => theme.colors.background.secondary};
  text-align: left;
  ${typographyMixin("body", 4)}
  color: ${({ theme }) => theme.colors.text.primary};
  ${phone} {
    display: block;
    padding: 0 ${spacing[0.5]} ${spacing[1]};
    border: 0;
    background: none;
  }
`;

const TONE: Record<GroupedTableTone, ReturnType<typeof css> | null> = {
  default: null,
  error: css`
    background: ${({ theme }) => theme.colors.error[50]};
  `,
  warning: css`
    background: ${({ theme }) => theme.colors.warning[50]};
  `,
  selected: css`
    background: ${({ theme }) => theme.colors.background.tertiary};
  `,
};

/** A card on a phone: title and aside on top, the other cells under them. */
const card = css`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${spacing[2]} ${spacing[3]};
  padding: ${spacing[3]};
  margin-bottom: ${spacing[2]};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  border-radius: ${borderRadius.xl};
  background: ${({ theme }) => theme.colors.background.primary};
`;

export const Row = styled.tr<{ $tone: GroupedTableTone; $hasNote: boolean }>`
  & > td {
    ${({ $tone }) => TONE[$tone]}
  }
  ${({ $hasNote }) =>
    $hasNote &&
    css`
      & > td {
        padding-bottom: ${spacing[1]};
      }
    `}
  ${phone} {
    ${card}
    ${({ $tone }) => TONE[$tone]}
    ${({ $tone, theme }) => $tone === "selected" && `box-shadow: inset 0 0 0 2px ${theme.colors.interactive.primary};`}
    ${({ $hasNote }) =>
      $hasNote &&
      css`
        margin-bottom: 0;
        border-bottom: 0;
        border-radius: ${borderRadius.xl} ${borderRadius.xl} 0 0;
      `}
    & > td {
      display: block;
      padding: 0;
      border: 0;
      background: none;
    }
  }
`;

export const Cell = styled.td<{ $fit?: boolean; $compact?: boolean }>`
  padding: ${({ $compact }) => ($compact ? `${spacing[3]} ${spacing[3.5]}` : `${spacing[2]} ${spacing[3]}`)};
  border-top: 1px solid ${({ theme }) => theme.colors.border.subtle};
  vertical-align: top;
  ${typographyMixin("body", 4)}
  color: ${({ theme }) => theme.colors.text.primary};
  ${({ $fit }) => $fit && "width: 1%; white-space: nowrap;"}
  ${phone} {
    min-width: 0;
    white-space: normal;
    &[data-phone="title"] {
      grid-column: 1;
    }
    &[data-phone="aside"] {
      grid-column: 2;
      grid-row: 1;
    }
    &[data-phone="stack"] {
      grid-column: 1 / -1;
    }
    &[data-phone="stack"]::before {
      content: attr(data-label);
      display: block;
      ${typographyMixin("caption", 2)}
      font-weight: ${fontWeights.semibold};
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: ${({ theme }) => theme.colors.text.secondary};
    }
  }
`;

/** The note under a row, joined to it (no line between them). */
export const NoteRow = styled.tr<{ $tone: GroupedTableTone }>`
  & > td {
    padding-top: 0;
    border-top: 0;
    ${({ $tone }) => TONE[$tone]}
  }
  ${phone} {
    display: block;
    padding: 0 ${spacing[3]} ${spacing[3]};
    margin-bottom: ${spacing[2]};
    border: 1px solid ${({ theme }) => theme.colors.border.subtle};
    border-top: 0;
    border-radius: 0 0 ${borderRadius.xl} ${borderRadius.xl};
    ${({ $tone }) => TONE[$tone]}
    & > td {
      display: block;
      padding: 0;
      background: none;
    }
  }
`;

/** One cell across every column: a placeholder row, or the empty message. */
export const FullRow = styled.tr<{ $tone: GroupedTableTone }>`
  & > td {
    ${({ $tone }) => TONE[$tone]}
  }
  ${phone} {
    ${card}
    grid-template-columns: minmax(0, 1fr);
    ${({ $tone }) => TONE[$tone]}
    & > td {
      display: block;
      padding: 0;
      border: 0;
      background: none;
    }
  }
`;

export const EmptyCell = styled.td`
  padding: ${spacing[6]} ${spacing[3]};
  text-align: center;
  ${typographyMixin("body", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;
